<template>
  <div
    class="vue-color-avatar"
    :style="{
      width: `${boxSize}px`,
      height: `${boxSize}px`,
      ...getWrapperShapeStyle(),
      transform: 'rotateY(-180deg)'
    }"
    :class="getWrapperShapeClassName()"
  >
    <Background :color="avatarOption.background.color" />

    <div class="avatar-payload" v-html="svgContent" />

    <!--    <Border-->
    <!--      :color="avatarOption.background.borderColor"-->
    <!--      :radius="getWrapperShapeStyle().borderRadius"-->
    <!--    />-->
  </div>
</template>

<script lang="ts" setup>
import { ref, toRefs, watchEffect } from 'vue'

import { WidgetType, WrapperShape } from '@/enums'
import type { AvatarOption } from '@/types'
import { getRandomAvatarOption } from '@/utils'
import { AVATAR_LAYER, NONE, SHAPE_STYLE_SET } from '@/utils/constant'
import { widgetData } from '@/utils/dynamic-data'

import Background from './widgets/Background.vue'
// import Border from './widgets/Border.vue'

const props = withDefaults(defineProps<{ boxSize?: number, size?: number, outputSize?: number }>(), {
  boxSize: 50,
  size: 280,
  outputSize: 180
})

const emit = defineEmits(['imageChange'])

const { size: avatarSize } = toRefs(props)

const avatarOption = ref<AvatarOption>(getRandomAvatarOption())

function getWrapperShapeClassName() {
  return {
    [WrapperShape.Circle]:
    avatarOption.value.wrapperShape === WrapperShape.Circle
    // [WrapperShape.Square]:
    //   avatarOption.value.wrapperShape === WrapperShape.Square,
    // [WrapperShape.Squircle]:
    //   avatarOption.value.wrapperShape === WrapperShape.Squircle,
  }
}

function getWrapperShapeStyle() {
  return SHAPE_STYLE_SET[avatarOption.value.wrapperShape!]
}

const svgContent = ref('')

function resetAvatarOption() {
  avatarOption.value = getRandomAvatarOption()
}

async function outputImage() {
  const avatarEle = document.querySelector('.vue-color-avatar') as HTMLDivElement
  if (!avatarEle) return

  try {
    const svgElement = avatarEle.querySelector('svg')
    if (!svgElement) return

    // 克隆 SVG 并设置尺寸
    const clonedSvg = svgElement.cloneNode(true) as SVGElement
    clonedSvg.setAttribute('width', `${props.outputSize}px`)
    clonedSvg.setAttribute('height', `${props.outputSize}px`)

    // 转换为字符串
    const svgString = new XMLSerializer().serializeToString(clonedSvg)
    const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    
    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = props.outputSize
      canvas.height = props.outputSize
      
      const ctx = canvas.getContext('2d')
      if (ctx) {
        // 创建圆形裁剪路径
        ctx.beginPath()
        ctx.arc(props.outputSize / 2, props.outputSize / 2, props.outputSize / 2, 0, Math.PI * 2)
        ctx.closePath()
        ctx.clip()

        // 绘制背景
        ctx.fillStyle = avatarOption.value.background.color
        ctx.fillRect(0, 0, props.outputSize, props.outputSize)

        // 保存当前状态
        ctx.save()
        
        // 设置镜像变换
        ctx.translate(props.outputSize, 0)
        ctx.scale(-1, 1)

        // 绘制图像
        ctx.drawImage(img, 0, 0, props.outputSize, props.outputSize)

        // 恢复状态
        ctx.restore()

        // 添加圆形边框
        ctx.strokeStyle = '#ffffff'
        ctx.lineWidth = 2
        ctx.stroke()

        emit('imageChange', canvas.toDataURL())
      }
      
      // 清理资源
      URL.revokeObjectURL(url)
    }
    img.src = url

  } catch (e) {
    console.error('Error generating avatar:', e)
  }
}

defineExpose({ resetAvatarOption })

// watch(
//   () => avatarOption.value,
//   () => nextTick(() => outputImage()),
//   {
//     immediate: true
//   }
// )

watchEffect(async () => {
  const sortedList = Object.entries(avatarOption.value.widgets).sort(
    ([prevShape, prev], [nextShape, next]) => {
      // @ts-ignore
      const ix = prev.zIndex ?? AVATAR_LAYER[prevShape]?.zIndex ?? 0
      // @ts-ignore
      const iix = next.zIndex ?? AVATAR_LAYER[nextShape]?.zIndex ?? 0
      return ix - iix
    }
  )

  // const promises: Promise<string>[] = sortedList.map(async ([widgetType, opt]) => {
  //   return (
  //     await import(`../assets/widgets/${widgetType}/${opt.shape}.svg?raw`)
  //   ).default
  // })

  const promises: Promise<string>[] = sortedList.map(
    async ([widgetType, opt]) => {
      // @ts-ignore
      if (opt.shape !== NONE && widgetData?.[widgetType]?.[opt.shape]) {
        // @ts-ignore
        return (await widgetData[widgetType][opt.shape]()).default
      }
      return ''
    }
  )

  let skinColor: string | undefined

  const svgRawList = await Promise.all(promises).then((raw) => {
    return raw.map((svgRaw, i) => {
      const [widgetType, widget] = sortedList[i]
      let widgetFillColor = widget.fillColor

      if (widgetType === WidgetType.Face) {
        skinColor = widgetFillColor
      }
      if (skinColor && widgetType === WidgetType.Ear) {
        widgetFillColor = skinColor
      }

      const content = svgRaw
        .slice(svgRaw.indexOf('>', svgRaw.indexOf('<svg')) + 1)
        .replace('</svg>', '')
        // @ts-ignore
        .replaceAll('$fillColor', widgetFillColor || 'transparent')

      return `
        <g id="vue-color-avatar-${sortedList[i][0]}">
          ${content}
        </g>
      `
    })
  })

  svgContent.value = `
    <svg
      width="${props.boxSize}"
      height="${props.boxSize}"
      viewBox="0 0 ${avatarSize.value / 0.7} ${avatarSize.value / 0.7}"
      preserveAspectRatio="xMidYMax meet"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g transform="translate(100, 65)">
        ${svgRawList.join('')}
      </g>
    </svg>
  `

  setTimeout(() => outputImage(), 100)
})
</script>

<style lang="scss" scoped>
.vue-color-avatar {
  position: relative;
  overflow: hidden;

  .avatar-payload {
    position: relative;
    z-index: 2;
    width: 100%;
    height: 100%;
  }
}
</style>
